import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";
import globalStore from "../../../state";
import { useWorkspaceService } from "./useWorkspaceService";
import { isFreePlan, isTrialPlan } from "@functions/workspacePlans";
import { trialDurationLabel } from "@functions/plans/trialConstants";

type ActivateTrialOptions = { funnelSource: string; feature?: string; onSuccess?: () => void };

type UseTrialResult = {
    isEligibleForTrial: boolean;
    isLoading: boolean;
    error: Error | null;
    activateTrial: (options: ActivateTrialOptions) => Promise<void>;
    isActivating: boolean;
};

declare const swal: any;

export function useTrial(): UseTrialResult {
    const usage = useStore(globalStore, (state) => state.getUsage());
    const uid = useStore(globalStore, (state) => state.user?.uid);
    const currentWorkspaceId = useStore(globalStore, (state) => state.currentWorkspaceId);
    const { activatePremiumTrial, checkHasUsedTrial } = useWorkspaceService();
    const [hasUsedTrial, setHasUsedTrial] = useState<boolean | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [isActivating, setIsActivating] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadTrialStatus() {
            if (!uid) {
                setHasUsedTrial(null);
                return;
            }

            const exists = await checkHasUsedTrial();

            if (cancelled) {
                return;
            }

            setHasUsedTrial(exists === null ? null : exists);
            setError(null);
        }

        loadTrialStatus().catch((loadError: unknown) => {
            if (cancelled) {
                return;
            }
            setHasUsedTrial(null);
            setError(loadError instanceof Error ? loadError : new Error(String(loadError)));
        });

        return () => {
            cancelled = true;
        };
    }, [uid, checkHasUsedTrial]);

    const isEligibleForTrial = useMemo(() => {
        if (!usage || hasUsedTrial === null) {
            return false;
        }

        if (!isFreePlan(usage.plan) || isTrialPlan(usage)) {
            return false;
        }

        if (usage.features?.allowMultipleTrials) {
            return true;
        }

        return !hasUsedTrial;
    }, [usage, hasUsedTrial]);

    const activateTrial = useCallback(
        async (options: ActivateTrialOptions) => {
            const { funnelSource, feature, onSuccess } = options;
            if (!currentWorkspaceId || isActivating) {
                return;
            }
            setIsActivating(true);
            try {
                await activatePremiumTrial(currentWorkspaceId, { funnelSource, feature });
                if (typeof window !== "undefined") {
                    localStorage.removeItem("sawTrialWelcomeModal");
                }
                swal.fire({
                    title: "Your Premium Trial Has Started",
                    text: `You now have access to premium features for ${trialDurationLabel}.`,
                    icon: "success",
                    timer: 4000
                });
                onSuccess?.();
            } catch (err) {
                swal.fire({
                    title: "Could Not Start Trial",
                    text: err?.message || "Unable to activate premium trial.",
                    icon: "error",
                    timer: 5000
                });
            } finally {
                setIsActivating(false);
            }
        },
        [currentWorkspaceId, isActivating, activatePremiumTrial]
    );

    return {
        isEligibleForTrial,
        isLoading: Boolean(uid) && hasUsedTrial === null,
        error,
        activateTrial,
        isActivating
    };
}
